import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Trash, Pencil, UploadSimple, Buildings } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = () => ({
  id: '', name: '', short: '', gst_enabled: false, gstin: '',
  state: 'Tamil Nadu', address: '', email: '', phone: '', accent: '#1D9E75', logo_url: '',
  bank: { beneficiary: '', account: '', bank_name: '', bank_address: '', ifsc: '', swift: '', upi: '' },
  signature_label: '', signatory: 'Authorised Signatory',
});

export const CompaniesPage = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/companies`, { withCredentials: true });
      setList(r.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await axios.put(`${API_URL}/api/companies/${form.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/companies`, form, { withCredentials: true });
      setOpen(false); setForm(empty()); setEdit(false); load();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const onEdit = (c) => {
    setForm({ ...empty(), ...c, bank: { ...empty().bank, ...(c.bank || {}) } });
    setEdit(true); setOpen(true);
  };
  const onDel = async (id) => {
    if (!window.confirm('Delete this company?')) return;
    await axios.delete(`${API_URL}/api/companies/${id}`, { withCredentials: true });
    load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? list.filter((c) => `${c.name} ${c.short || ''} ${c.gstin || ''} ${c.state || ''}`.toLowerCase().includes(q)) : list;
  }, [list, search]);
  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'companies-page-size', testIdPrefix: 'company' });

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file || !form.id) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/api/companies/${form.id}/logo`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ ...form, logo_url: r.data.logo_url });
    } catch (err) { alert('Logo upload failed: ' + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Companies</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Your billing entities (logo, GSTIN, bank, signatory)</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{edit ? 'Edit Company' : 'Add Company'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="co-name" /></div>
                <div><Label>Short Code *</Label><Input required value={form.short} placeholder="DXT" onChange={(e) => setForm({ ...form, short: e.target.value.toUpperCase() })} data-testid="co-short" /></div>
                <div className="flex items-center gap-3 col-span-2">
                  <Switch checked={form.gst_enabled} onCheckedChange={(v) => setForm({ ...form, gst_enabled: v })} data-testid="co-gst-toggle" />
                  <Label>GST-enabled company</Label>
                </div>
                {form.gst_enabled && (
                  <div className="col-span-2"><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="co-gstin" /></div>
                )}
                <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Accent Color (hex)</Label><Input value={form.accent} placeholder="#1D9E75" onChange={(e) => setForm({ ...form, accent: e.target.value })} /></div>
                <div className="col-span-2"><Label>Address</Label>
                  <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
              </div>

              {edit && (
                <div className="border-t pt-3">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.logo_url && (
                      <img src={form.logo_url.startsWith('http') ? form.logo_url : `${API_URL}${form.logo_url}`}
                        alt="logo" style={{ height: 40, maxWidth: 160, objectFit: 'contain' }} />
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn">
                      <UploadSimple size={14} className="mr-1" /> {form.logo_url ? 'Replace Logo' : 'Upload Logo'}
                    </Button>
                  </div>
                  <p className="text-[10px] mt-2" style={{ color: 'var(--dx-text3)' }}>PNG/JPG/WEBP/SVG, max 5 MB. Will appear on invoices for this company.</p>
                </div>
              )}

              <div className="border-t pt-3">
                <h3 className="font-semibold text-sm mb-2 uppercase tracking-wider" style={{ color: 'var(--dx-text2)' }}>Bank Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Beneficiary Name</Label><Input value={form.bank.beneficiary} onChange={(e) => setForm({ ...form, bank: { ...form.bank, beneficiary: e.target.value } })} /></div>
                  <div><Label>Account Number</Label><Input value={form.bank.account} onChange={(e) => setForm({ ...form, bank: { ...form.bank, account: e.target.value } })} /></div>
                  <div><Label>Bank Name</Label><Input value={form.bank.bank_name} onChange={(e) => setForm({ ...form, bank: { ...form.bank, bank_name: e.target.value } })} /></div>
                  <div><Label>IFSC</Label><Input value={form.bank.ifsc} onChange={(e) => setForm({ ...form, bank: { ...form.bank, ifsc: e.target.value.toUpperCase() } })} /></div>
                  <div className="col-span-2"><Label>Bank Address</Label><Input value={form.bank.bank_address} onChange={(e) => setForm({ ...form, bank: { ...form.bank, bank_address: e.target.value } })} /></div>
                  <div><Label>SWIFT / BIC</Label><Input value={form.bank.swift} onChange={(e) => setForm({ ...form, bank: { ...form.bank, swift: e.target.value.toUpperCase() } })} /></div>
                  <div><Label>UPI ID</Label><Input value={form.bank.upi} onChange={(e) => setForm({ ...form, bank: { ...form.bank, upi: e.target.value } })} /></div>
                </div>
              </div>

              <div className="border-t pt-3 grid grid-cols-2 gap-3">
                <div><Label>Signature Label</Label><Input value={form.signature_label} placeholder="For DEXTRA TECHNOLOGIES" onChange={(e) => setForm({ ...form, signature_label: e.target.value })} /></div>
                <div><Label>Signatory</Label><Input value={form.signatory} placeholder="Authorised Signatory" onChange={(e) => setForm({ ...form, signatory: e.target.value })} /></div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-company-btn">{edit ? 'Update' : 'Save'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        <div className="p-3 border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <Input placeholder="Search companies by name, GSTIN, state…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="company-search" />
        </div>
        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead>#</TableHead>
            <TableHead>LOGO</TableHead><TableHead>NAME</TableHead><TableHead>SHORT</TableHead>
            <TableHead>GSTIN</TableHead><TableHead>STATE</TableHead><TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>{list.length === 0 ? 'No companies yet.' : 'No matches.'}</TableCell></TableRow>
            ) : paged.map((c, i) => (
              <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                <TableCell className="text-gray-400">{startIdx + i + 1}</TableCell>
                <TableCell>
                  {c.logo_url ? (
                    <img src={c.logo_url.startsWith('http') ? c.logo_url : `${API_URL}${c.logo_url}`}
                      alt="logo" style={{ height: 56, maxWidth: 180, objectFit: 'contain' }} />
                  ) : <Buildings size={28} style={{ color: 'var(--dx-text3)' }} />}
                </TableCell>
                <TableCell className="font-semibold">{c.name}</TableCell>
                <TableCell><span className="px-2 py-1 rounded text-[10px] font-bold uppercase" style={{ background: (c.accent || '#1D9E75') + '22', color: c.accent || '#1D9E75' }}>{c.short}</span></TableCell>
                <TableCell>{c.gstin || (c.gst_enabled ? '—' : <span className="text-[10px] uppercase" style={{ color: 'var(--dx-text3)' }}>Non-GST</span>)}</TableCell>
                <TableCell>{c.state || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`edit-company-${c.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(c.id)}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};

export default CompaniesPage;
